import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toast';
import { LuCopy, LuExternalLink, LuGlobe, LuPlus } from 'react-icons/lu';
import axiosInstance from '../../api/axiosInstance';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import { dateFormat } from '../../utils/dateFormat';
import {
  Breadcrumbs,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  PageScaffold,
  RowActions,
  SearchInput,
} from '../ui';
import '../../styles/environment.css';

const splitUrl = (raw = '') => {
  try {
    const u = new URL(raw);
    return { host: u.host, rest: `${u.pathname}${u.search}`.replace(/^\/$/, '') };
  } catch {
    return { host: raw, rest: '' };
  }
};

/**
 * CONFIGURE · Environments — list of runtime targets for the project.
 */
const EnvironmentsWorkspace = () => {
  const navigate = useNavigate();
  const { project_id: projectId } = useParams();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();

  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const pathFor = useCallback(
    (subPath) => {
      const workspaceId = getWorkspaceIdFromURL();
      return workspaceId
        ? buildWorkspaceDashboardPath(workspaceId, getProjectIdFromURL() || projectId, subPath)
        : `/dashboard/${subPath}`;
    },
    [getWorkspaceIdFromURL, getProjectIdFromURL, projectId]
  );

  const load = useCallback(async () => {
    if (!projectId) {
      setError('Select a project to manage environments.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/user_profile/list_environments', {
        params: { project_id: projectId },
      });
      if (res?.status_code !== 200) throw new Error(res?.message || 'Failed to load environments');
      setEnvironments(
        (res?.data || []).map((env) => ({
          id: env.id,
          name: env.environment_name,
          description: env.environment_description,
          url: env.target_url,
          created_at: env.created_at,
        }))
      );
    } catch (err) {
      console.error('Failed to fetch environments', err);
      setError(err?.message || 'Failed to load environments');
      setEnvironments([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return environments;
    return environments.filter((env) =>
      [env.name, env.description, env.url].some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [environments, search]);

  const goCreate = () => navigate(pathFor(`project-environment/${projectId}`));
  const goEdit = (env) => navigate(pathFor(`project-environment/${projectId}?env_id=${env.id}`));

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied');
    } catch {
      toast.error('Could not copy URL');
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const res = await axiosInstance.delete('/user_profile/delete_environment', {
        params: { env_id: pendingDelete.id },
      });
      if (res?.status_code !== 200) throw new Error(res?.message || 'Failed to delete environment');
      toast.success(`Deleted “${pendingDelete.name}”`);
      setEnvironments((prev) => prev.filter((e) => e.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (err) {
      console.error('Error deleting environment:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to delete environment');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Environment',
      render: (env) => (
        <div className="rt-env-cell">
          <span className="rt-env-cell__icon" aria-hidden="true">
            <LuGlobe size={16} />
          </span>
          <div className="rt-env-cell__text">
            <div className="rt-env-cell__name" title={env.name}>{env.name}</div>
            {env.description && (
              <div className="rt-env-cell__desc" title={env.description}>{env.description}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'url',
      header: 'Target URL',
      render: (env) => {
        const { host, rest } = splitUrl(env.url);
        return (
          <div className="rt-env-url">
            <span className="rt-env-url__text" title={env.url}>
              <span className="rt-env-url__host">{host}</span>
              {rest && <span className="rt-env-url__path">{rest}</span>}
            </span>
            <button
              type="button"
              className="rt-env-url__btn"
              onClick={(e) => {
                e.stopPropagation();
                copyUrl(env.url);
              }}
              title="Copy URL"
              aria-label={`Copy URL for ${env.name}`}
            >
              <LuCopy size={14} />
            </button>
            <a
              className="rt-env-url__btn"
              href={env.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="Open in new tab"
              aria-label={`Open ${env.name} in new tab`}
            >
              <LuExternalLink size={14} />
            </a>
          </div>
        );
      },
    },
    {
      key: 'created',
      header: 'Created',
      width: 140,
      render: (env) => <span className="rt2-muted">{dateFormat(env.created_at) || '—'}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      width: 120,
      render: (env) => (
        <div onClick={(e) => e.stopPropagation()} role="presentation">
          <RowActions
            primary={
              <button type="button" className="rt-btn rt-btn--secondary rt-btn--sm" onClick={() => goEdit(env)}>
                Edit
              </button>
            }
            menuItems={[
              {
                id: 'open',
                label: 'Open URL',
                onClick: () => window.open(env.url, '_blank', 'noopener,noreferrer'),
              },
              { id: 'copy', label: 'Copy URL', onClick: () => copyUrl(env.url) },
              {
                id: 'delete',
                label: 'Delete',
                danger: true,
                dividerBefore: true,
                onClick: () => setPendingDelete(env),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const createButton = (
    <button type="button" className="rt-btn rt-btn--primary" onClick={goCreate} disabled={!projectId}>
      <LuPlus size={16} aria-hidden="true" /> Create Environment
    </button>
  );

  return (
    <PageScaffold
      title="Environments"
      description="Configure target URLs and runtime environments for this project."
      breadcrumbs={<Breadcrumbs items={[{ label: 'Configure' }, { label: 'Environments' }]} />}
      actions={environments.length > 0 ? createButton : null}
    >
      {error && !loading ? (
        <ErrorState title="Unable to load environments" description={error} onRetry={load} />
      ) : !loading && environments.length === 0 ? (
        <div className="rt-env-empty">
          <EmptyState
            icon={<LuGlobe size={20} />}
            title="No environments yet"
            description="Add a target such as Development, Staging or Production so test runs know which URL to open."
            action={createButton}
          />
        </div>
      ) : (
        <>
          <FilterBar>
            <SearchInput value={search} onChange={setSearch} placeholder="Search by name, description or URL…" />
            {!loading && (
              <span className="rt-env-count">
                {filtered.length === environments.length
                  ? `${environments.length} environment${environments.length === 1 ? '' : 's'}`
                  : `${filtered.length} of ${environments.length}`}
              </span>
            )}
          </FilterBar>
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey="id"
            loading={loading}
            skeletonRows={5}
            onRowClick={goEdit}
            emptyTitle="No matching environments"
            emptyDescription={`Nothing matches “${search.trim()}”.`}
          />
        </>
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete environment?"
        body={`Delete “${pendingDelete?.name}”? Test runs that target it will need a new environment.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        danger
        onCancel={() => !deleting && setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </PageScaffold>
  );
};

export default EnvironmentsWorkspace;
